import { useCallback, useMemo } from "react";
import {
  CountQuery,
  CountQueryVariables,
  EditMessageThreadSeenMutation,
  EditMessageThreadSeenMutationVariables,
  MessageThreadFragment,
  MessageThreadType,
} from "gql/graphql";
import { useMutation } from "@apollo/client";
import EDIT_MESSAGE_THREAD_SEEN_MUTATION from "modules/messaging/_networking/edit-message-thread-seen";
import {
  UNSEEN_CHATS_COUNT_SEARCH_FILTERS,
  UNSEEN_MESSAGE_THREAD_COUNT_SEARCH_FILTERS,
} from "modules/utils/Constants";
import COUNT_QUERY from "./_networking/count";
import useMessageThreadCache from "./use-message-thread-cache";

interface IProps {
  messageThread?: MessageThreadFragment | null;
}

const useToggleMessageThreadSeen = ({ messageThread }: IProps) => {
  const { updateMessageThreadQueryCache } = useMessageThreadCache();

  const [editMessageThreadSeenAPI] = useMutation<
    EditMessageThreadSeenMutation,
    EditMessageThreadSeenMutationVariables
  >(EDIT_MESSAGE_THREAD_SEEN_MUTATION);

  const searchFilterListToUpdate = useMemo(() => {
    switch (messageThread?.type) {
      case MessageThreadType.Thread:
        return UNSEEN_MESSAGE_THREAD_COUNT_SEARCH_FILTERS;
      case MessageThreadType.Chat:
        return UNSEEN_CHATS_COUNT_SEARCH_FILTERS;
      case MessageThreadType.Task:
        //TODO: Impl
        return [];
      default:
        return [];
    }
  }, [messageThread?.type]);

  const toggleSeen = useCallback(
    (_isSeen?: boolean) => {
      if (!messageThread) return;
      const isSeen = _isSeen || !messageThread.isSeen;
      editMessageThreadSeenAPI({
        variables: {
          id: messageThread.id,
          isSeen,
        },
        optimisticResponse: {
          editMessageThread: {
            __typename: "MessageThread",
            ...messageThread,
            isSeen,
            unseenCount: isSeen ? 0 : messageThread.unseenCount,
          },
        },
        update: (cache, { data }) => {
          if (!data) return;
          const messageThread = data?.editMessageThread;
          if (!messageThread) {
            return;
          }
          cache.updateQuery<CountQuery, CountQueryVariables>(
            {
              query: COUNT_QUERY,
              variables: {
                searchFilterList: searchFilterListToUpdate,
              },
            },
            (data): CountQuery | null => {
              if (!data) return null;
              const count = data.count;
              return {
                ...data,
                count: count + (isSeen ? -1 : 1),
              };
            },
          );
          updateMessageThreadQueryCache(messageThread);
        },
        refetchQueries: [COUNT_QUERY],
      });
    },
    [
      editMessageThreadSeenAPI,
      messageThread,
      searchFilterListToUpdate,
      updateMessageThreadQueryCache,
    ],
  );

  return { toggleSeen };
};

export default useToggleMessageThreadSeen;
